import {useUser} from '../use-user';
import {ProgressCircle} from '@ui/progress/progress-circle';
import {SocialLoginPanel} from './social-login-panel';
import {BasicInfoPanel} from './basic-info-panel/basic-info-panel';
import {ChangePasswordPanel} from './change-password-panel/change-password-panel';
import {LocalizationPanel} from './localization-panel';
import {AccessTokenPanel} from './access-token-panel/access-token-panel';
import {DangerZonePanel} from './danger-zone-panel/danger-zone-panel';
import {Trans} from '@ui/i18n/trans';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {AccountSettingsSidenav} from '@common/auth/ui/account-settings/account-settings-sidenav';
import {SessionsPanel} from '@common/auth/ui/account-settings/sessions-panel/sessions-panel';
import {useContext} from 'react';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {TwoFactorPanel} from '@common/auth/ui/account-settings/two-factor-panel';
import {IccdbNavbar} from '@app/discovery/iccdb-navbar';
import {IccdbFooter} from '@app/discovery/iccdb-footer';
import {useThemeSelector} from '@ui/themes/theme-selector-context';

export function AccountSettingsPage() {
  const {auth} = useContext(SiteConfigContext);
  const {selectedTheme} = useThemeSelector();
  const isDarkMode = selectedTheme?.is_dark ?? true;

  const {data, isLoading} = useUser('me', {
    with: ['roles', 'social_profiles', 'tokens'],
  });

  const pageBg = isDarkMode ? '#020617' : '#f8fafc';
  const textColor = isDarkMode ? '#f8fafc' : '#0f172a';
  const textMuted = isDarkMode ? '#94a3b8' : '#64748b';
  const containerBorder = isDarkMode ? '#1e293b' : '#e2e8f0';

  return (
    <div
      style={{
        backgroundColor: pageBg,
        color: textColor,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        transition: 'background-color 0.2s, color 0.2s',
      }}
    >
      <StaticPageTitle>
        <Trans message="Account Settings" />
      </StaticPageTitle>

      <IccdbNavbar />

      <main style={{flex: 1, width: '100%', maxWidth: '1360px', margin: '0 auto', padding: '32px 20px 64px'}}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: '20px',
            marginBottom: '32px',
            paddingBottom: '24px',
            borderBottom: `1px solid ${containerBorder}`,
          }}
        >
          <div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                backgroundColor: 'rgba(245, 158, 11, 0.12)',
                color: '#f59e0b',
                border: '1px solid rgba(245, 158, 11, 0.25)',
                marginBottom: '10px',
              }}
            >
              <i className="fa-solid fa-sliders" style={{ fontSize: '10px' }}></i>
              User Profile & Security
            </div>
            <h1
              style={{
                fontSize: '32px',
                fontWeight: 900,
                letterSpacing: '-0.02em',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: textColor,
              }}
            >
              <span
                style={{
                  width: '6px',
                  height: '32px',
                  borderRadius: '9999px',
                  background: 'linear-gradient(to bottom, #f59e0b, #e11d48)',
                  display: 'inline-block',
                }}
              />
              Account Settings
            </h1>
            <p style={{ fontSize: '14px', color: textMuted, marginTop: '8px', marginBottom: 0, maxWidth: '640px' }}>
              View and update your personal details, profile image, security credentials, active sessions, and preferences.
            </p>
          </div>
        </div>

        {/* Content Body */}
        {isLoading || !data ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
            <ProgressCircle
              aria-label="Loading user.."
              isIndeterminate
            />
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row items-start gap-32">
            <AccountSettingsSidenav />
            <div className="flex-1 w-full min-w-0">
              {auth.accountSettingsPanels?.map(panel => (
                <panel.component key={panel.id} user={data.user} />
              ))}
              <BasicInfoPanel user={data.user} />
              <SocialLoginPanel user={data.user} />
              <ChangePasswordPanel />
              <TwoFactorPanel user={data.user} />
              <SessionsPanel />
              <LocalizationPanel user={data.user} />
              <AccessTokenPanel user={data.user} />
              <DangerZonePanel />
            </div>
          </div>
        )}
      </main>

      <IccdbFooter />
    </div>
  );
}
